import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls, secondaryButtonCls, destructiveButtonCls } from "../../components/ui-components/buttonStyles.js";
import Modal from "../../components/ui-components/Modal";
import FormButton from "../../components/form-components/FormButton";
import RejectDrawer from "./components/RejectDrawer";
import Skeleton from "../../components/ui-components/Skeleton";
import StatusPill from "../Budgets/components/StatusPill";
import DecisionSummary from "./components/DecisionSummary";
import FinancialImpactPanel from "./components/FinancialImpactPanel";
import JustificationPanel from "./components/JustificationPanel";
import ScopeContextPanel from "./components/ScopeContextPanel";
import ApprovalProgressPanel from "./components/ApprovalProgressPanel";
import GovernancePolicyPanel from "./components/GovernancePolicyPanel";
import RequesterPanel from "./components/RequesterPanel";
import {
  useApprovalRequestQuery,
  useApproveApprovalRequestMutation,
  useRejectApprovalRequestMutation,
  useReturnApprovalRequestMutation,
  useCancelApprovalRequestMutation,
} from "./hooks/useApprovalsQueries";
import { toast } from "../../components/ui-components/Toast";
import { useCurrentUser } from "../../api/auth";

const OBJECT_TYPE_LABELS = {
  budget_period_version: "Budget Version",
  budget_creation: "Budget Creation",
  budget: "Budget",
};

const formatObjectDisplay = (objectType, objectId) => {
  if (!objectType && !objectId) return "Approval Request";
  const label = OBJECT_TYPE_LABELS[objectType] || objectType || "Object";
  const id = objectId ?? "";
  return id ? `${label} #${id}` : label;
};

const resolveUserDisplay = (userId, usersMap) => {
  if (!userId || !usersMap) return { name: userId || "Unknown", title: "" };
  const u = usersMap[String(userId)];
  if (!u) return { name: userId, title: "" };
  const parts = [u.first_name, u.last_name].filter(Boolean);
  const name = parts.length > 0 ? parts.join(" ") : userId;
  return { name, title: u.function || "" };
};

const ApprovalRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // null when closed; "reject" | "return" selects which comment-required decision the
  // shared drawer is collecting (both reuse the same required-comment prompt pattern).
  const [commentDrawerAction, setCommentDrawerAction] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null);

  const { data: request, isLoading, error, refetch } = useApprovalRequestQuery(id);
  const { data: currentUser } = useCurrentUser();
  const approveMutation = useApproveApprovalRequestMutation();
  const rejectMutation = useRejectApprovalRequestMutation();
  const returnMutation = useReturnApprovalRequestMutation();
  const cancelMutation = useCancelApprovalRequestMutation();

  const isPending = request?.status === "pending" || request?.status === "submitted";

  // Hard SoD, mirrored from the backend's checkHardSoD: the preparer of a request may not
  // decide it themselves (approve, reject, or return all count as "deciding").
  const preparerId = request?.created_by ?? request?.requested_by;
  const isPreparer =
    !!currentUser?.id && !!preparerId && String(currentUser.id) === String(preparerId);

  const handleApprove = async () => {
    setErrorBanner(null);
    try {
      await approveMutation.mutateAsync({ id, comment: undefined });
      toast.success("Approved");
      refetch();
    } catch (err) {
      if (err?.status === 409) {
        setErrorBanner("This request was updated by someone else. Refreshed.");
        refetch();
      }
    }
  };

  const handleCommentDrawerSubmit = async (comment) => {
    setErrorBanner(null);
    const isReturn = commentDrawerAction === "return";
    try {
      if (isReturn) {
        await returnMutation.mutateAsync({ id, comment });
        toast.success("Returned to preparer");
      } else {
        await rejectMutation.mutateAsync({ id, comment });
        toast.success("Rejected");
      }
      setCommentDrawerAction(null);
      refetch();
    } catch (err) {
      if (err?.status === 409) {
        setErrorBanner("This request was updated by someone else. Refreshed.");
        refetch();
      }
    }
  };

  const handleCancel = async () => {
    setErrorBanner(null);
    try {
      await cancelMutation.mutateAsync(id);
      toast.success("Cancelled");
      setCancelModalOpen(false);
      refetch();
    } catch (err) {
      if (err?.status === 403) {
        toast.error("Only the requester can cancel this request");
      }
      if (err?.status === 409) {
        setErrorBanner("This request was updated by someone else. Refreshed.");
        refetch();
      }
    }
  };

  const isMutating =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    returnMutation.isPending ||
    cancelMutation.isPending;

  const headerTitle = request
    ? formatObjectDisplay(request.object_type, request.object_id)
    : "Approval Request";

  useSetHeaderRecordName(request ? headerTitle : null);

  if (isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={400} />
      </div>
    );
  }

  if (error || !request) {
    const isUnauthorized = error?.status === 401 || error?.status === 403;
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <div className="sticky top-0 z-40 border-b border-border flex items-center h-[64px] pl-8 pr-4">
          <span className="text-lg text-text font-semibold font-['Plus_Jakarta_Sans',sans-serif]">
            Approval Request
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-danger mb-2">
              {isUnauthorized ? "Not authorized" : "Approval request not found"}
            </div>
            <FormButton
              title="Back to inbox"
              style="secondary"
              onClick={() => navigate("/approvals")}
            />
          </div>
        </div>
      </div>
    );
  }

  const policySnapshot = request.policy_snapshot_json || {};
  const usersMap = request.users || {};
  const requester = resolveUserDisplay(request.created_by, usersMap);
  const objCtx = request.object_context || {};

  const STATUS_ICON_MAP = {
    approved: icon({ name: "circle-check", family: "sharp", style: "light" }),
    rejected: icon({ name: "circle-xmark", family: "sharp", style: "light" }),
    cancelled: icon({ name: "circle-minus", family: "sharp", style: "light" }),
  };
  const statusIcon = STATUS_ICON_MAP[request.status] || STATUS_ICON_MAP.cancelled;

  // Header action buttons (governance row): a single filled-slate primary (Approve),
  // a neutral secondary (Cancel request), and a neutral-by-default destructive (Reject).
  // Styles are shared across the governance pages — see buttonStyles.js.
  const primaryBtnCls = primaryButtonCls;
  const secondaryBtnCls = secondaryButtonCls;
  const destructiveBtnCls = destructiveButtonCls;

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      {/* Action buttons live in the global top bar (next to the tab switcher), like /budgets/:id. */}
      <HeaderPortal>
        {isPending && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              className={secondaryBtnCls}
              onClick={() => setCancelModalOpen(true)}
              disabled={isMutating}
            >
              Cancel request
            </button>
            {/* Preparer can't decide their own request (self-approval) — matches backend
                checkHardSoD, which blocks approve/reject/return alike for the preparer. */}
            {!isPreparer && (
              <>
                <button
                  type="button"
                  className={secondaryBtnCls}
                  onClick={() => setCommentDrawerAction("return")}
                  disabled={isMutating}
                >
                  Return
                </button>
                <button
                  type="button"
                  className={destructiveBtnCls}
                  onClick={() => setCommentDrawerAction("reject")}
                  disabled={isMutating}
                >
                  Reject
                </button>
                <button
                  type="button"
                  className={primaryBtnCls}
                  onClick={handleApprove}
                  disabled={isMutating}
                >
                  {approveMutation.isPending ? "Approving..." : "Approve"}
                </button>
              </>
            )}
          </div>
        )}
      </HeaderPortal>

      {/* Title + status live in the header's row-1 title slot. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {headerTitle}
          </div>
          <StatusPill status={request.status} />
        </div>
      </HeaderPortal>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-auto p-6">
        {errorBanner && (
          <div className="mb-4 px-4 py-3 bg-warning-soft border border-warning rounded-card text-sm text-text">
            {errorBanner}
            <button
              onClick={() => setErrorBanner(null)}
              className="ml-2 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* ── SoD status banner: preparer can't decide their own request ── */}
        {isPending && isPreparer && (
          <div className="mb-6 px-4 py-3 bg-warning-soft border border-warning rounded-card text-sm text-text">
            You prepared this request — you can&apos;t approve it
          </div>
        )}

        {/* ── Terminal status banner (non-pending) ── */}
        {!isPending && (
          <div className="mb-6 px-4 py-3 bg-card border border-card-bd rounded-card flex items-center gap-3">
            <FontAwesomeIcon icon={statusIcon} className="text-text-2 text-lg" />
            <div>
              <p className="text-sm font-medium text-text">
                {request.status === "approved" && "This request has been approved"}
                {request.status === "rejected" && "This request has been rejected"}
                {request.status === "cancelled" && "This request has been cancelled"}
                {!["approved", "rejected", "cancelled"].includes(request.status) &&
                  `This request is ${request.status}`}
              </p>
              <p className="text-xs text-text-2">
                {request.status === "approved" && "All required approval steps have been completed."}
                {request.status === "rejected" && "An approver has rejected this request. The requester may submit a new one."}
                {request.status === "cancelled" && "This request was cancelled by the requester."}
                {!["approved", "rejected", "cancelled"].includes(request.status) &&
                  "No further actions are available for this request."}
              </p>
            </div>
          </div>
        )}

        {/* ── Decision Summary ── */}
        <DecisionSummary
          budgetName={objCtx.budget_name}
          periodLabel={objCtx.period_label}
          impactAmount={request.impact_amount}
          impactCurrency={request.impact_currency}
          previousAmount={objCtx.previous_amount}
          requesterName={requester.name}
          requesterTitle={requester.title}
          requestedAt={request.created_at}
        />

        {/* ── Two-column layout: Decision Context (left) + Governance Mechanics (right) ── */}
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-[1fr_1fr] mb-6">
          {/* Left column — decision context */}
          <div className="flex flex-col gap-6">
            <FinancialImpactPanel
              impactAmount={request.impact_amount}
              impactCurrency={request.impact_currency}
              previousAmount={objCtx.previous_amount}
            />
            <JustificationPanel reason={request.reason} />
            {/* TODO: wire scope fields once available from API */}
            <ScopeContextPanel />
          </div>

          {/* Right column — governance mechanics */}
          <div className="flex flex-col gap-6">
          <GovernancePolicyPanel
              policySnapshot={policySnapshot}
              steps={request.steps}
              impactAmount={request.impact_amount}
            />
          <ApprovalProgressPanel
              steps={request.steps}
              usersMap={usersMap}
              policySnapshot={policySnapshot}
            />
            <RequesterPanel
              requesterName={requester.name}
              requesterTitle={requester.title}
              createdAt={request.created_at}
            />
          </div>
        </div>

        {/* ── Footer info bar ── */}
        <div className="flex items-center justify-between text-xs text-text-2 px-1 pb-4">
          <span className="uppercase tracking-wider">
            Request ID: {request.id || "—"}
          </span>
          <div className="flex items-center gap-4">
            <span className="uppercase tracking-wider text-text-3 cursor-default">
              View audit log
            </span>
          </div>
        </div>
      </div>

      <RejectDrawer
        open={commentDrawerAction !== null}
        action={commentDrawerAction || "reject"}
        onClose={() => setCommentDrawerAction(null)}
        onSubmit={handleCommentDrawerSubmit}
        isSubmitting={
          commentDrawerAction === "return" ? returnMutation.isPending : rejectMutation.isPending
        }
      />

      <Modal
        show={cancelModalOpen}
        close={() => setCancelModalOpen(false)}
        title="Cancel approval request?"
        description="This will cancel the pending approval. The requester can create a new request after edits."
        confirmButton="Cancel request"
        cancelButton="Keep"
        confirmAction={handleCancel}
        icon={icon({ name: "circle-xmark", style: "solid" })}
      />
    </div>
  );
};

export default ApprovalRequestDetailPage;
